import React from "react";
import { cn } from "@/lib/utils";

/**
 * Reusable section wrapper with consistent padding + container width.
 * Light-theme defaults; pass tone="surface" for soft grey-blue bg, tone="dark" for dark accent section.
 */
export function Section({
    children,
    className = "",
    containerClass = "",
    id,
    tone = "default",
    testId,
}) {
    const toneClass =
        tone === "surface"
            ? "bg-[#F7F8FA]"
            : tone === "dark"
              ? "bg-[#0a0b0d] text-white"
              : "bg-white";
    return (
        <section
            id={id}
            data-testid={testId}
            className={cn("relative py-16 sm:py-20 lg:py-24", toneClass, className)}
        >
            <div
                className={cn(
                    "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8",
                    containerClass,
                )}
            >
                {children}
            </div>
        </section>
    );
}

/** Small spec-style label above headings */
export function SpecLabel({ children, className = "", tone = "default" }) {
    const colorClass =
        tone === "dark" ? "text-white/65" : "text-[#4a5562]";
    return (
        <div
            className={cn(
                "inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-display font-bold",
                colorClass,
                className,
            )}
        >
            <span
                className="inline-block h-[2px] w-7 bg-[#188bf6]"
                aria-hidden="true"
            />
            <span>{children}</span>
        </div>
    );
}

/** Section heading group: optional spec label + heading + optional kicker */
export function SectionHeading({
    label,
    title,
    description,
    align = "left",
    tone = "default",
    className = "",
}) {
    const titleColor = tone === "dark" ? "text-white" : "text-[#0a0b0d]";
    const descColor = tone === "dark" ? "text-white/70" : "text-[#4a5562]";
    return (
        <div
            className={cn(
                "max-w-3xl",
                align === "center" ? "mx-auto text-center" : "",
                className,
            )}
        >
            {label ? <SpecLabel tone={tone}>{label}</SpecLabel> : null}
            <h2
                className={cn(
                    "mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight",
                    titleColor,
                )}
            >
                {title}
            </h2>
            {description ? (
                <p
                    className={cn(
                        "mt-4 text-base sm:text-lg leading-relaxed",
                        descColor,
                    )}
                >
                    {description}
                </p>
            ) : null}
        </div>
    );
}
